import {
  Injectable,
  inject,
  signal,
  computed,
  effect,
  afterNextRender,
  DOCUMENT,
} from '@angular/core';
import { LoggerService } from '@core/logging';
import { UserPreferencesStore } from '@core/user-preferences';
import { Theme } from '@core/user-preferences';

const THEME_STORAGE_KEY = 'blisterr.admin.theme';
const DARK_CLASS = 'dark';
const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * Gère l'application du thème UI admin (LIGHT / DARK / SYSTEM).
 *
 * Ordre de précédence au boot :
 *  1. localStorage (lecture optimiste, instantané — pas de flash de thème).
 *  2. Préférence backend (charge async via `UserPreferencesStore`) — écrase
 *     `localStorage` si différente (le serveur gagne au load pour cohérence
 *     cross-device).
 *  3. Fallback : `Theme.SYSTEM`.
 *
 * `setTheme()` :
 *  - met à jour le signal,
 *  - écrit dans `localStorage`,
 *  - applique/retire la classe `.dark` sur `<html>`,
 *  - push la préférence vers le backend (fire-and-forget, log on error).
 *
 * Admin est CSR pur (cf. admin/CLAUDE.md), mais on garde `afterNextRender`
 * pour éviter tout side-effect pendant le bootstrap initial (cohérence avec
 * le pattern webapp).
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly preferencesStore = inject(UserPreferencesStore);
  private readonly logger = inject(LoggerService);
  private readonly doc = inject(DOCUMENT);

  private readonly _currentTheme = signal<Theme>(Theme.SYSTEM);
  private readonly _systemDark = signal(false);

  readonly currentTheme = this._currentTheme.asReadonly();

  readonly appliedTheme = computed<'LIGHT' | 'DARK'>(() => {
    const current = this._currentTheme();
    if (current === Theme.SYSTEM) {
      return this._systemDark() ? 'DARK' : 'LIGHT';
    }
    return current;
  });

  constructor() {
    afterNextRender(() => this.initBrowser());

    effect(() => {
      this.applyDomClass(this.appliedTheme());
    });

    // Sync depuis le backend une fois les préférences chargées.
    effect(() => {
      if (!this.preferencesStore.isLoaded()) return;

      const remote = this.preferencesStore.theme();
      if (remote !== this._currentTheme()) {
        this._currentTheme.set(remote);
        this.writeToStorage(remote);
      }
    });
  }

  /**
   * Change le thème localement et synchronise le backend (fire-and-forget).
   * Le sync backend n'est tenté que si les préférences sont chargées
   * (i.e. utilisateur authentifié).
   */
  setTheme(theme: Theme): void {
    this._currentTheme.set(theme);
    this.writeToStorage(theme);

    if (!this.preferencesStore.isLoaded()) return;

    void this.preferencesStore
      .updatePreferences({ theme })
      .catch(err => this.logger.warn('ThemeService', 'Failed to sync theme', { err }));
  }

  private initBrowser(): void {
    const stored = this.readFromStorage();
    if (stored) {
      this._currentTheme.set(stored);
    }

    const mediaQuery = this.doc.defaultView?.matchMedia(SYSTEM_DARK_QUERY);
    if (mediaQuery) {
      this._systemDark.set(mediaQuery.matches);
      mediaQuery.addEventListener('change', e => this._systemDark.set(e.matches));
    }
  }

  private applyDomClass(applied: 'LIGHT' | 'DARK'): void {
    const root = this.doc.documentElement;
    if (applied === 'DARK') {
      root.classList.add(DARK_CLASS);
    } else {
      root.classList.remove(DARK_CLASS);
    }
  }

  private readFromStorage(): Theme | null {
    try {
      const raw = this.doc.defaultView?.localStorage.getItem(THEME_STORAGE_KEY);
      if (raw === Theme.LIGHT || raw === Theme.DARK || raw === Theme.SYSTEM) {
        return raw;
      }
      return null;
    } catch {
      return null;
    }
  }

  private writeToStorage(theme: Theme): void {
    try {
      this.doc.defaultView?.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch (err: unknown) {
      this.logger.warn('ThemeService', 'localStorage write failed', { err });
    }
  }
}
